/**
 * useServerPagination — thin server-mode pagination adapter.
 *
 * A BE lista-végpontok ma ad-hoc `{ page, pageSize, hasMore, total }` shape-et
 * adnak (BE-S2 = A döntés, 2026-06-08: a DataTable adaptál a meglévő shape-hez,
 * 0 BE-munka). Ez az adapter az EGYETLEN hely, ami a server-pagination kontraktust
 * a DataTable (`@tanstack/react-table`) `PaginationState`-jére fordítja — ha a BE
 * valaha standardizál (option B), csak ez a fájl változik, nem minden fogyasztó.
 *
 * Használat egy lista-oldalon:
 *   const sp = useServerPagination(20);
 *   const { invoices, total } = useInvoices({ ...filters, ...sp.query });
 *   useEffect(() => sp.reset(), [filters.search, filters.status]); // szűrő-váltáskor 1. oldal
 *   <DataTable data={invoices} {...sp.tableProps(total)} />
 */
import type { OnChangeFn, PaginationState } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';

/**
 * A THIN belső server-list pagination kontraktus. A lista-API-k `meta` mezője
 * ezt a shape-et adja; ez a típus dokumentálja a kötést (DataTable a `total`-ból
 * származtatja a pageCount-ot, így a `hasMore` redundáns, de a kontraktus része).
 */
export interface ServerListMeta {
  /** 1-alapú aktuális oldal */
  page: number;
  pageSize: number;
  /** összes találat (szűrt) — ebből jön a DataTable rowCount */
  total: number;
  /** van-e még oldal (a total-ból is levezethető; a BE-shape része) */
  hasMore: boolean;
}

/** DataTable server-mode propok, amiket a `tableProps(total)` ad vissza. */
export interface ServerPaginationTableProps {
  manualPagination: true;
  rowCount: number;
  pagination: PaginationState;
  onPaginationChange: OnChangeFn<PaginationState>;
}

export interface ServerPaginationController {
  /** aktuális PaginationState (0-alapú pageIndex) */
  pagination: PaginationState;
  /** közvetlen setter (DataTable footer ezt hívja pageSize/page váltáskor) */
  setPagination: OnChangeFn<PaginationState>;
  /** API-filter fragment — spread-eld a lista-hook filtereibe (`page` 1-alapú) */
  query: { page: number; pageSize: number };
  /** DataTable server-mode propok a `total` ismeretében */
  tableProps: (total: number) => ServerPaginationTableProps;
  /** ugrás az 1. oldalra (hívd szűrő-váltáskor); no-op ha már ott vagyunk */
  reset: () => void;
}

/**
 * @param initialPageSize kezdeti oldalméret (default 20, a BE-default-tal egyezik)
 */
export function useServerPagination(initialPageSize = 20): ServerPaginationController {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: initialPageSize,
  });

  const reset = useCallback(() => {
    setPagination(prev => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }));
  }, []);

  const query = useMemo(
    () => ({ page: pagination.pageIndex + 1, pageSize: pagination.pageSize }),
    [pagination.pageIndex, pagination.pageSize]
  );

  const tableProps = useCallback(
    (total: number): ServerPaginationTableProps => ({
      manualPagination: true,
      rowCount: total,
      pagination,
      onPaginationChange: setPagination,
    }),
    [pagination]
  );

  return { pagination, setPagination, query, tableProps, reset };
}
